"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarVerbatims } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";
import cardStyles from "./AvatarDetailCard/AvatarDetailCard.module.css";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  params: { lang: string };
};

const quotePlaceholder = [
  { cells: [{ width: "85%" }] },
  { cells: [{ width: "70%" }] },
  { cells: [{ width: "75%" }] },
];

const tagsPlaceholder = [
  { cells: [{ width: "80px" }, { width: "110px" }, { width: "70px" }] },
  { cells: [{ width: "90px" }, { width: "65px" }] },
];

export default function AvatarDetailVerbatims(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, isAnalyzing = false, params } = props;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: verbatimsData, isLoading: isFetching } = useFetchAvatarVerbatims({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = { ...avatar, ...(verbatimsData || {}) } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const handleSaveList = async (fieldKey: AvatarField, newItems: string[]) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.VERBATIMS,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const handleSaveText = async (fieldKey: AvatarField, val: string) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, fieldKey, val);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.VERBATIMS,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_PRIVATE)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={quotePlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.privateQuotes || []}
              onSave={(items) => handleSaveList(AvatarField.PRIVATE_QUOTES, items)}
              listClassName={styles.quoteList}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_PUBLIC)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={quotePlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.publicQuotes || []}
              onSave={(items) => handleSaveList(AvatarField.PUBLIC_QUOTES, items)}
              listClassName={styles.quoteList}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_KEYWORDS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={tagsPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.dominantKeywords || []}
              onSave={(items) => handleSaveList(AvatarField.DOMINANT_KEYWORDS, items)}
              listClassName={styles.tagsList}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_EMOTIONAL)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={tagsPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.emotionalLanguage || []}
              onSave={(items) => handleSaveList(AvatarField.EMOTIONAL_LANGUAGE, items)}
              listClassName={styles.tagsList}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <AvatarDetailCard
        title={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_COPY_ADVICE)}
        isPlaceholder={isPlaceholder}
        placeholderConfig={[
          { cells: [{ width: "100%" }] },
          { cells: [{ width: "90%" }] },
          { cells: [{ width: "75%" }] },
        ]}
      >
        {isPlaceholder ? null : (
          <EditableText
            value={fullAvatar.copyAdvice || ""}
            onSave={(val) => handleSaveText(AvatarField.COPY_ADVICE, val)}
            className={cardStyles.textBody}
            multiline={true}
            maxLength={Limits.MAX_AVATAR_PARAGRAPH}
            placeholder={T(TKey.CLIENT_AVATAR_DETAIL_VERBATIMS_COPY_ADVICE_PLACEHOLDER)}
            disabled={isAnalyzing || !canEdit}
          />
        )}
      </AvatarDetailCard>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
